import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";
import { useIsMobile } from "../../hooks/useIsMobile";
import CardListMobile from "../../templates/mobile/CardListMobile";
import { db } from "../../config/firebase";

const COLLECTION_INSTALACOES = "financeiro_propostas_instalacoes";
const COLLECTION_OS = "financeiro_propostas_os";
const COLLECTION_PREVENTIVAS = "financeiro_propostas_preventivas";

const STATUS_OPTIONS = [
  { value: "CRIADA", label: "Proposta Criada" },
  { value: "ENVIADA", label: "Enviada" },
  { value: "APROVADA", label: "Aprovada" },
  { value: "REPROVADA", label: "Reprovada" },
  { value: "FINALIZADA", label: "Finalizada" },
];

const NATUREZA_OPTIONS = [
  { value: "Instalação", label: "Instalação" },
  { value: "Ordens de Serviço", label: "Ordens de Serviço" },
  { value: "Preventivas", label: "Preventivas" },
];

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusLabel(status) {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
}

export default function FinanceiroGerenciarPropostasPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const isMobile = useIsMobile(900);

  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [campoFiltro, setCampoFiltro] = useState("clienteNome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const nivel = nivelUsuario(usuario);
        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
        const empresaId = empresaAtual?.id || usuario?.empresaId || null;

        // Função auxiliar para buscar proposals de uma coleção
        const buscarPropostas = async (collectionName, natureza) => {
          const qBase = query(
            collection(db, collectionName),
            where("grupoId", "==", usuario.grupoId)
          );

          const snap = await getDocs(qBase);
          let lista = snap.docs.map((d) => ({
            id: d.id,
            natureza,
            collection: collectionName,
            ...d.data(),
          }));

          if (restringirEmpresa && empresaId) {
            const empresaRef = String(empresaId);
            lista = lista.filter((item) => {
              const clienteRef = item?.clienteId != null ? String(item.clienteId) : "";
              const empresaItemRef = item?.empresaId != null ? String(item.empresaId) : "";
              return clienteRef === empresaRef || empresaItemRef === empresaRef;
            });
          }

          return lista;
        };

        // Buscar de todas as 3 coleções
        const results = await Promise.allSettled([
          buscarPropostas(COLLECTION_INSTALACOES, "Instalação"),
          buscarPropostas(COLLECTION_OS, "Ordens de Serviço"),
          buscarPropostas(COLLECTION_PREVENTIVAS, "Preventivas"),
        ]);

        const [instalacoesRes, osRes, preventivasRes] = results;
        const instalacoes = instalacoesRes.status === "fulfilled" ? instalacoesRes.value : [];
        const os = osRes.status === "fulfilled" ? osRes.value : [];
        const preventivas = preventivasRes.status === "fulfilled" ? preventivasRes.value : [];

        // Combinar todos os registros
        const todos = [...instalacoes, ...os, ...preventivas];
        setRegistros(todos);

        const teveFalha = results.some((r) => r.status === "rejected");
        if (teveFalha) {
          setErro("Algumas coleções de propostas não puderam ser carregadas. Exibindo os dados disponíveis.");
        }
      } catch (e) {
        console.error("Erro ao carregar propostas:", e);
        setErro("Erro ao carregar propostas.");
        setRegistros([]);
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, usuario?.empresaId, empresaAtual?.id, modoGlobal]);

  const camposFiltro = [
    { value: "clienteNome", label: "Cliente" },
    { value: "academiaNome", label: "Academia" },
    { value: "natureza", label: "Natureza" },
    { value: "status", label: "Status" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;

    return registros.filter((r) => {
      const base = String(r?.[campoFiltro] ?? "").toLowerCase();
      return base.includes(texto);
    });
  }, [registros, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filtrados,
    "clienteNome"
  );

  const itemsParaCards = useMemo(
    () =>
      sortedData.map((registro) => ({
        id: `${registro.collection}-${registro.id}`,
        nome: registro.clienteNome || "Sem cliente",
        subtitulo: registro.natureza || "-",
        status: String(registro.status || "").toLowerCase(),
        detalhes: [
          { icone: "🏢", label: "Academia", valor: registro.academiaNome || "—" },
          { icone: "📌", label: "Status", valor: statusLabel(registro.status) || "—" },
          { icone: "💰", label: "Valor", valor: `R$ ${formatMoney(registro.valorTotal)}` },
        ],
      })),
    [sortedData]
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        titulo="Propostas"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          titulo="Propostas Consolidadas"
          searchPlaceholder="Buscar propostas..."
          emptyMessage="Nenhuma proposta encontrada."
          hideDefaultActions
          getSearchText={(item) => `${item.nome} ${item.subtitulo} ${item?.detalhes?.map((d) => d.valor).join(" ") || ""}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Gerenciar"
      subtitulo="Financeiro / Gerenciar / Propostas"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div>
            <h3>Propostas Consolidadas</h3>
            <p>Visualize todas as propostas (Instalações, OS e Preventivas) em um único lugar.</p>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar propostas..."
          mostrarAdicionar={false}
        />

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead>
              <tr>
                <SortableHeader
                  label="Cliente"
                  columnKey="clienteNome"
                  onSort={requestSort}
                  direction={getSortDirection("clienteNome")}
                />
                <SortableHeader
                  label="Academia"
                  columnKey="academiaNome"
                  onSort={requestSort}
                  direction={getSortDirection("academiaNome")}
                />
                <SortableHeader
                  label="Natureza"
                  columnKey="natureza"
                  onSort={requestSort}
                  direction={getSortDirection("natureza")}
                />
                <SortableHeader
                  label="Status"
                  columnKey="status"
                  onSort={requestSort}
                  direction={getSortDirection("status")}
                />
                <SortableHeader
                  label="Valor"
                  columnKey="valorTotal"
                  onSort={requestSort}
                  direction={getSortDirection("valorTotal")}
                />
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16, textAlign: "center" }}>
                    Carregando propostas...
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16, textAlign: "center" }}>
                    Nenhuma proposta encontrada.
                  </td>
                </tr>
              ) : (
                sortedData.map((registro) => (
                  <tr key={`${registro.collection}-${registro.id}`}>
                    <td>{registro.clienteNome || "—"}</td>
                    <td>{registro.academiaNome || "—"}</td>
                    <td>{registro.natureza || "—"}</td>
                    <td>{statusLabel(registro.status)}</td>
                    <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                      R$ {formatMoney(registro.valorTotal)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
